import { useCallback, useId, type ReactNode } from "react";
import type { ActivityRow } from "@gioia365/contracts";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTab } from "../api/queries";
import { ConversationsProblem, ConversationsSearchField } from "../conversations/ConversationParts";
import { Button } from "../design/components/Button";
import { Chip } from "../design/components/Chip";
import { DataTable } from "../design/components/DataTable";
import { Icon } from "../design/components/Icon";
import { Reason } from "../design/components/Reason";
import { Segmented } from "../design/components/Segmented";
import { Select } from "../design/components/Select";
import { TextField } from "../design/components/TextField";
import { formatClockDuration, formatNumericDate, formatNumericDateTime, localDayOf } from "../i18n/format";
import { useLocale, useT, type Translate } from "../i18n/locale";
import { useResultSearch } from "../leads/lead-list-hooks";
import { useLeadView } from "../leads/lead-queries";
import { PeoplePicker } from "../reports/ReportControls";
import { shiftDay } from "../reports/report-search";
import { activityScopeOf, useAccess } from "../session/access";
import { activityPagesQuery, activityPeopleQuery } from "./activity-queries";
import {
  activityQueryOf,
  ACTIVITY_PERIOD_ORDER,
  ACTIVITY_PERIOD_PARAMS,
  chosenPeopleOf,
  DEFAULT_ACTIVITY_PERIOD,
  GROUP_ORDER,
  GROUP_PARAMS,
  groupParamOf,
  validateActivitySearch,
  type ActivityPeriodParam,
  type ActivitySearch,
} from "./activity-search";
import { rowViewOf, timelineLeadOf, timelineTargetOf } from "./activity-texts";
import "../design/components/page-header.css";
import "../leads/lead-list.css";
import "../conversations/conversations.css";
import "../reports/reports.css";
import "./activity.css";

// «Attività» («Chi ha fatto cosa», SEZ-8; M7 parte 2): il registro di tutte le azioni, anche automatiche, una riga per azione, dalla
// più recente, con lo stile di «Conversazioni» (scelta di Claude: niente mock-up, Lucio ha chiesto di procedere spediti). Ogni riga
// dice quando, chi (una persona, «Gioia365 · nome» per il gestore della piattaforma, «automatico» per la sequenza di chiamate, Meta e
// gli altri automatismi, «da Close» per lo storico), che cosa in parole comuni e il Lead, con il link alla scheda e «Apri nella
// Timeline», che apre la scheda sull'elemento (`?evento=`). Periodo (SEZ-3, di partenza «Oggi»), «Di chi» (soltanto per chi vede il
// team, SEZ-1), «Che cosa» e la ricerca del Lead stanno nell'indirizzo (`activity-search.ts`); «Mostra altre azioni» legge la pagina
// dopo. Sotto i 720 px di contenuto ogni riga è una scheda, come in «Conversazioni».

/** Lo stesso indirizzo senza i valori tolti: un filtro tornato a «Tutti» non resta scritto. */
function compact(search: ActivitySearch): ActivitySearch {
  return Object.fromEntries(Object.entries(search).filter(([, value]) => value !== undefined)) as ActivitySearch;
}

export function ActivityPage() {
  const t = useT();
  const access = useAccess();
  const search = validateActivitySearch(useSearch({ from: "/autenticato/guscio/azienda/attivita" }));
  const navigate = useNavigate({ from: "/attivita" });
  const scope = activityScopeOf(access);
  const update = useCallback(
    (change: Partial<ActivitySearch>) => void navigate({ search: (previous) => compact({ ...validateActivitySearch(previous), ...change }), replace: true }),
    [navigate],
  );
  const watching = access.context.commercialView;
  let intro: string;
  if (watching) intro = t("activity.introWatching", { name: watching.displayName });
  else intro = scope === "all" ? t("activity.introTeam") : t("activity.introOwn");

  return (
    <div className="g-conversations g-activity">
      <div className="g-page-head g-conversations__head">
        <div>
          <p className="g-page-head__eyebrow">{t("shell.nav.activity")}</p>
          <h1 className="g-page-head__title">{t("activity.title")}</h1>
          {scope === "none" ? null : <p className="g-conversations__intro">{intro}</p>}
        </div>
      </div>
      {scope === "none" ? (
        <div className="g-conversations__empty">
          <p>
            <strong>{t("activity.noAccessTitle")}</strong>
          </p>
          <Reason tone="warning">{t("activity.noAccess")}</Reason>
        </div>
      ) : (
        <ActivityList search={search} update={update} byPerson={scope === "all"} />
      )}
    </div>
  );
}

type ListProps = { search: ActivitySearch; update: (change: Partial<ActivitySearch>) => void; byPerson: boolean };

function ActivityList({ search, update, byPerson }: ListProps) {
  const t = useT();
  const tab = useTab();
  const view = useLeadView();
  const baseId = useId();
  const access = useAccess();
  const timeZone = access.context.settings.timeZone;
  const query = activityQueryOf(search, byPerson);
  const pages = useInfiniteQuery({ ...activityPagesQuery(tab, query, view), placeholderData: keepPreviousData });
  const people = useQuery({ ...activityPeopleQuery(tab, view), enabled: byPerson });
  const writeQ = useCallback((next: string | undefined) => update({ q: next }), [update]);
  const { text, setText } = useResultSearch(search.q, writeQ);
  const shown = pages.data && !pages.isPlaceholderData ? pages.data.pages.flatMap((page) => page.rows) : null;
  const groupOptions = [{ value: "all", label: t("activity.allGroups") }, ...GROUP_ORDER.map((group) => ({ value: groupParamOf(group), label: t(`activity.groups.${group}`) }))];

  let results: ReactNode;
  if (shown === null) {
    results = pages.isError ? (
      <ConversationsProblem error={pages.error} onRetry={() => void pages.refetch()} />
    ) : (
      <p className="g-conversations__loading" role="status">
        {t("common.loading")}
      </p>
    );
  } else if (shown.length === 0) {
    results = (
      <div className="g-conversations__empty">
        <p>
          <strong>{t("activity.empty")}</strong>
        </p>
        <p>{t("activity.emptyHint")}</p>
      </div>
    );
  } else {
    results = (
      <>
        {pages.isError ? <ConversationsProblem error={pages.error} onRetry={() => void pages.refetch()} /> : null}
        <div className="g-conversations__table g-activity__table">
          <DataTable caption={t("activity.caption")}>
            <thead>
              <tr>
                <th scope="col">{t("activity.columns.when")}</th>
                <th scope="col">{t("activity.columns.who")}</th>
                <th scope="col">{t("activity.columns.what")}</th>
                <th scope="col">{t("activity.columns.lead")}</th>
                <th scope="col">
                  <span className="g-visually-hidden">{t("activity.columns.action")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <ActivityRowView key={row.key} row={row} timeZone={timeZone} />
              ))}
            </tbody>
          </DataTable>
        </div>
        {pages.hasNextPage ? (
          <div>
            <Button onClick={() => void pages.fetchNextPage()} disabled={pages.isFetchingNextPage}>
              {pages.isFetchingNextPage ? t("activity.loadingMore") : t("activity.more")}
            </Button>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <>
      <div className="g-conversations__toolbar">
        <ActivityPeriod search={search} update={update} timeZone={timeZone} />
        <div className="g-conversations__filters">
          <Select
            id={`${baseId}-group`}
            label={t("activity.group")}
            value={search.cosa ?? "all"}
            options={groupOptions}
            onChange={(value) => update({ cosa: Object.hasOwn(GROUP_PARAMS, value) ? (value as keyof typeof GROUP_PARAMS) : undefined })}
          />
        </div>
        <ConversationsSearchField id={`${baseId}-search`} text={text} onChange={setText} />
      </div>
      {byPerson && people.data ? <PeoplePicker people={people.data} chosen={chosenPeopleOf(search)} update={update} /> : null}
      <div className="g-conversations__results" aria-busy={shown === null && !pages.isError ? true : undefined}>
        {results}
      </div>
    </>
  );
}

/** Il primo e l'ultimo giorno di un periodo pronto, contando da `today`; nessuno per «Personalizzato». */
function periodDays(period: ActivityPeriodParam, today: string): { from: string; to: string } | null {
  switch (period) {
    case "oggi":
      return { from: today, to: today };
    case "ieri":
      return { from: shiftDay(today, -1), to: shiftDay(today, -1) };
    case "settimana":
      return { from: shiftDay(today, -6), to: today };
    case "mese":
      return { from: shiftDay(today, -29), to: today };
    default:
      return null;
  }
}

/** Un giorno scritto in un campo data: `AAAA-MM-GG` negli anni che il server accetta; un campo vuoto o a metà no. */
const isDay = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value) && value >= "1900-01-01" && value <= "9998-12-31";

/** Il periodo (SEZ-3): i pulsanti, i giorni del periodo scelto e, con «Personalizzato», «Dal» e «Al», sempre in ordine. */
function ActivityPeriod({ search, update, timeZone }: { search: ActivitySearch; update: ListProps["update"]; timeZone: string }) {
  const t = useT();
  const baseId = useId();
  const today = localDayOf(timeZone, new Date().toISOString());
  const period = search.periodo ?? DEFAULT_ACTIVITY_PERIOD;
  const days = periodDays(period, today);

  function choose(value: string) {
    const next = ACTIVITY_PERIOD_ORDER.find((candidate) => candidate === value);
    if (next === undefined || next === period) return;
    if (next === "personalizzato") update({ periodo: next, dal: days?.from ?? today, al: days?.to ?? today });
    else update({ periodo: next === DEFAULT_ACTIVITY_PERIOD ? undefined : next, dal: undefined, al: undefined });
  }

  function changeFrom(value: string) {
    if (!isDay(value)) return;
    const al = search.al ?? today;
    update({ periodo: "personalizzato", dal: value, al: value > al ? value : al });
  }

  function changeTo(value: string) {
    if (!isDay(value)) return;
    const dal = search.dal ?? today;
    update({ periodo: "personalizzato", al: value, dal: value < dal ? value : dal });
  }

  let detail: ReactNode = null;
  if (period === "personalizzato") {
    detail = (
      <div className="g-conversations__days">
        <TextField id={`${baseId}-from`} label={t("conversations.fromDay")} type="date" min="1900-01-01" max="9998-12-31" value={search.dal ?? today} onChange={(event) => changeFrom(event.target.value)} />
        <TextField id={`${baseId}-to`} label={t("conversations.toDay")} type="date" min="1900-01-01" max="9998-12-31" value={search.al ?? today} onChange={(event) => changeTo(event.target.value)} />
      </div>
    );
  } else if (days) {
    const from = formatNumericDate(timeZone, days.from);
    detail = <p className="g-conversations__period-days">{days.from === days.to ? t("conversations.periodDay", { day: from }) : t("conversations.periodDays", { from, to: formatNumericDate(timeZone, days.to) })}</p>;
  }

  return (
    <div className="g-conversations__period">
      <Segmented
        label={t("conversations.periodLabel")}
        options={ACTIVITY_PERIOD_ORDER.map((id) => ({ value: id, label: t(`conversations.periods.${ACTIVITY_PERIOD_PARAMS[id]}`) }))}
        value={period}
        onChange={choose}
      />
      {detail}
    </div>
  );
}

/** Chi ha fatto l'azione: la persona (con la «Guarda come…» in cui l'ha fatta), il gestore di Gioia365, un automatismo, Close. */
function WhoCell({ t, row }: { t: Translate; row: ActivityRow }) {
  const name = row.actor.displayName.trim() || t("timeline.unknownAuthor");
  let sub: string | null = null;
  if (row.actor.kind === "system") sub = t("activity.automatic");
  else if (row.actor.fromClose) sub = t("activity.fromClose");
  else if (row.viewOf !== null) sub = t("activity.viewOf", { name: row.viewOf });
  return (
    <>
      {row.actor.kind === "platform" ? <Chip tone="accent">{t("activity.platformActor", { name })}</Chip> : name}
      {sub ? <span className="g-table__sub">{sub}</span> : null}
    </>
  );
}

function ActivityRowView({ row, timeZone }: { row: ActivityRow; timeZone: string }) {
  const t = useT();
  const { locale } = useLocale();
  const nameId = useId();
  const view = rowViewOf(t, locale, timeZone, row);
  const target = timelineTargetOf(row);
  const duration = row.kind === "call" && !row.live && row.durationSeconds !== null ? formatClockDuration(row.durationSeconds) : null;
  const detail = [view.detail, duration].filter((part): part is string => part !== null).join(" · ");

  const cardLead = timelineLeadOf(row);
  let lead: ReactNode;
  if (row.lead !== null) {
    // Attivo: il nome apre la scheda. Archiviato: non ha più una scheda. Unito: il nome apre il Lead in cui è entrato, se si vede.
    let state: string | null = null;
    if (row.lead.state === "archived") state = t("activity.leadArchived");
    else if (row.lead.state === "merged") state = row.lead.mergedInto ? t("activity.leadMergedInto", { name: row.lead.mergedInto.displayName ?? "—" }) : t("activity.leadMerged");
    lead = (
      <>
        {cardLead !== null ? (
          <Link to="/lead/$leadId" params={{ leadId: cardLead }} id={nameId}>
            {row.lead.displayName ?? "—"}
          </Link>
        ) : (
          <span id={nameId} className="g-conversations__lead">
            {row.lead.displayName ?? "—"}
          </span>
        )}
        {row.lead.salonName ? <span className="g-table__sub">{row.lead.salonName}</span> : null}
        {state ? <span className="g-table__sub">{state}</span> : null}
      </>
    );
  }
  else if (row.leadHidden) lead = <span className="g-activity__muted">{t("activity.hiddenLead")}</span>;
  else lead = t("activity.noLead");

  return (
    <tr>
      <td data-label={t("activity.columns.when")}>{formatNumericDateTime(timeZone, row.occurredAt)}</td>
      <td data-label={t("activity.columns.who")}>
        <WhoCell t={t} row={row} />
      </td>
      <th scope="row">
        <span className="g-activity__what">
          <Icon name={view.icon} />
          <span>{view.title}</span>
        </span>
        {detail ? <span className="g-table__sub">{detail}</span> : null}
      </th>
      <td data-label={t("activity.columns.lead")}>{lead}</td>
      <td className="g-conversations__action">
        {cardLead !== null && target !== null ? (
          <Link to="/lead/$leadId" params={{ leadId: cardLead }} search={{ evento: `${target.kind}:${target.id}` }} className="g-button" aria-describedby={nameId}>
            {t("activity.openInTimeline")}
          </Link>
        ) : null}
      </td>
    </tr>
  );
}
